import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Plus, Search, Edit2, Trash2, Shield, UserCheck, UserX,
  Crown, Truck, UserCog, ShoppingBag, Megaphone, Store, Lock, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import usePermissions from '@/hooks/usePermissions';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Role type icons mapping
const ROLE_TYPE_ICONS = {
  admin: { icon: Crown, color: 'text-yellow-400', bg: 'bg-yellow-400/10' },
  manager: { icon: UserCog, color: 'text-blue-400', bg: 'bg-blue-400/10' },
  sales: { icon: ShoppingBag, color: 'text-green-400', bg: 'bg-green-400/10' },
  driver: { icon: Truck, color: 'text-orange-400', bg: 'bg-orange-400/10' },
  marketing: { icon: Megaphone, color: 'text-purple-400', bg: 'bg-purple-400/10' },
  vendor: { icon: Store, color: 'text-cyan-400', bg: 'bg-cyan-400/10' }
};

const CreateUserDialog = ({ onSuccess, roles = [], seatUsage, onSeatLimit }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role_id: '',
  });

  const isFull = seatUsage && seatUsage.total_limit > 0 && seatUsage.remaining <= 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.role_id) {
      toast.error('Please select a role');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/users`, {
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        roles: [], // No built-in roles
        custom_role_id: formData.role_id
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('User created successfully');
      setOpen(false);
      setFormData({ email: '', password: '', full_name: '', role_id: '' });
      onSuccess();
    } catch (error) {
      // 402 Payment Required → user limit reached → escalate to upgrade flow
      if (error.response?.status === 402) {
        const detail = error.response?.data?.detail || {};
        if (detail.error === 'USER_LIMIT_REACHED') {
          setOpen(false);
          if (onSeatLimit) onSeatLimit(detail);
          return;
        }
      }
      toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const selectedRole = roles.find(r => r.id === formData.role_id);
  const roleTypeConfig = selectedRole ? ROLE_TYPE_ICONS[selectedRole.role_type] : null;

  return (
    <>
      <Button 
        onClick={() => {
          if (isFull) {
            if (onSeatLimit) onSeatLimit({ current: seatUsage.used, limit: seatUsage.total_limit, plan_limit: seatUsage.plan_limit, addon_seats: seatUsage.addon_seats });
            return;
          }
          setOpen(true);
        }}
        className="bg-wt-gold hover:bg-wt-gold-hover text-black"
        data-testid="create-user-button"
        title={isFull ? 'Plan seat limit reached — upgrade to invite more' : undefined}
      >
        {isFull ? <Lock className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
        Add User
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl">
              Create New User
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-wt-text-primary">Email *</Label>
              <Input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary"
                placeholder="user@example.com"
              />
            </div>

            <div>
              <Label className="text-wt-text-primary">Full Name *</Label>
              <Input
                required
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary"
                placeholder="John Doe"
              />
            </div>

            <div>
              <Label className="text-wt-text-primary">Password *</Label>
              <Input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary"
                placeholder="Minimum 6 characters"
                minLength={6}
              />
            </div>

            <div>
              <Label className="text-wt-text-primary">Role *</Label>
              <Select 
                value={formData.role_id} 
                onValueChange={(value) => setFormData({ ...formData, role_id: value })}
              >
                <SelectTrigger className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="user-role-select">
                  <SelectValue placeholder="Select a role..." />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  {roles.length === 0 ? (
                    <div className="p-3 text-center text-wt-text-muted text-sm">
                      No roles available. Create roles first.
                    </div>
                  ) : (
                    roles.map((role) => {
                      const typeConfig = ROLE_TYPE_ICONS[role.role_type] || ROLE_TYPE_ICONS.sales;
                      const Icon = typeConfig.icon;
                      return (
                        <SelectItem key={role.id} value={role.id} className="text-wt-text-primary">
                          <div className="flex items-center gap-2">
                            <Icon className={`h-4 w-4 ${typeConfig.color}`} />
                            <span>{role.name}</span>
                          </div>
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
              {selectedRole && (
                <p className="text-xs text-wt-text-muted mt-1">
                  Type: {selectedRole.role_type?.charAt(0).toUpperCase() + selectedRole.role_type?.slice(1)} • 
                  {selectedRole.permissions?.length || 0} permissions
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="text-wt-text-secondary">
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading || roles.length === 0}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              >
                {loading ? 'Creating...' : 'Create User'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

const EditUserDialog = ({ user, onSuccess, roles = [] }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    role_id: '',
  });

  useEffect(() => {
    if (open && user) {
      setFormData({
        full_name: user.full_name || '',
        role_id: user.custom_role_id || '',
      });
    }
  }, [open, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.role_id) {
      toast.error('Please select a role');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/users/${user.id}`, {
        full_name: formData.full_name,
        roles: [], // Clear built-in roles
        custom_role_id: formData.role_id
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('User updated successfully');
      setOpen(false);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const selectedRole = roles.find(r => r.id === formData.role_id);

  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setOpen(true)}
        className="text-wt-text-muted hover:text-wt-gold"
        data-testid={`edit-user-${user.id}`}
      >
        <Edit2 className="h-4 w-4" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl">
              Edit User
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-wt-text-primary">Email</Label>
              <Input
                value={user?.email || ''}
                disabled
                className="mt-2 bg-wt-subtle border-wt-border text-wt-text-muted"
              />
            </div>

            <div>
              <Label className="text-wt-text-primary">Full Name *</Label>
              <Input
                required
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>

            <div>
              <Label className="text-wt-text-primary">Role *</Label>
              <Select 
                value={formData.role_id} 
                onValueChange={(value) => setFormData({ ...formData, role_id: value })}
              >
                <SelectTrigger className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="edit-user-role-select">
                  <SelectValue placeholder="Select a role..." />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  {roles.map((role) => {
                    const typeConfig = ROLE_TYPE_ICONS[role.role_type] || ROLE_TYPE_ICONS.sales;
                    const Icon = typeConfig.icon;
                    return (
                      <SelectItem key={role.id} value={role.id} className="text-wt-text-primary">
                        <div className="flex items-center gap-2">
                          <Icon className={`h-4 w-4 ${typeConfig.color}`} />
                          <span>{role.name}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {selectedRole && (
                <p className="text-xs text-wt-text-muted mt-1">
                  Type: {selectedRole.role_type?.charAt(0).toUpperCase() + selectedRole.role_type?.slice(1)} • 
                  {selectedRole.permissions?.length || 0} permissions
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="text-wt-text-secondary">
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

const UserManagementPage = () => {
  const { user: currentUser } = useAuth();
  const { hasPermission, isAdmin } = usePermissions();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [seatUsage, setSeatUsage] = useState(null); // {used, plan_limit, addon_seats, total_limit, remaining}
  const [upgradeDialog, setUpgradeDialog] = useState(null); // {current, limit, plan_limit, addon_seats}
  const [purchasingAddon, setPurchasingAddon] = useState(false);

  // Permission-based access
  const canCreateUsers = hasPermission('users.create');
  const canEditUsers = hasPermission('users.edit');
  const canDeleteUsers = hasPermission('users.delete');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [usersRes, rolesRes, seatRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/users`, { headers }),
        axios.get(`${API_BASE_URL}/roles`, { headers }),
        axios.get(`${API_BASE_URL}/company/user-limit`, { headers }).catch(() => ({ data: null })),
      ]);
      
      setUsers(usersRes.data);
      // Filter to only custom roles (not system roles)
      const customRoles = (rolesRes.data.roles || []).filter(r => !r.is_system);
      setRoles(customRoles);
      setSeatUsage(seatRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseAddon = async (qty = 1) => {
    setPurchasingAddon(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE_URL}/company/addon-users/purchase`, { quantity: qty }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { free_plan } = res.data || {};
      if (free_plan) {
        toast.success(`${qty} seat${qty > 1 ? 's' : ''} added (free plan — no charge)`);
      } else {
        toast.success(`${qty} seat${qty > 1 ? 's' : ''} added — billed at the end of every cycle while active.`);
      }
      setUpgradeDialog(null);
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = (typeof detail === 'object' ? detail?.message : detail) || 'Failed to add seats';
      toast.error(msg);
    } finally {
      setPurchasingAddon(false);
    }
  };

  const handleRemoveAddon = async (qty = 1) => {
    if (!window.confirm(`Remove ${qty} add-on seat${qty > 1 ? 's' : ''}? Next cycle's invoice will reflect the new count.`)) return;
    setPurchasingAddon(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE_URL}/company/addon-users/remove`, { quantity: qty }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const removed = res.data?.removed || 0;
      if (removed > 0) {
        toast.success(`${removed} seat${removed > 1 ? 's' : ''} removed. Won't be billed next cycle.`);
      } else {
        toast.info(res.data?.reason || 'No add-on seats to remove');
      }
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = (typeof detail === 'object' ? detail?.message : detail) || 'Failed to remove seats';
      toast.error(msg);
    } finally {
      setPurchasingAddon(false);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/users/${userId}/toggle-status`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`User ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      loadData();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('User deleted successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const filteredUsers = users.filter(user =>
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeUsers = users.filter(u => u.is_active !== false).length;
  const inactiveUsers = users.filter(u => u.is_active === false).length;

  const getUserRole = (user) => {
    if (user.custom_role_id) {
      const role = roles.find(r => r.id === user.custom_role_id);
      return role || null;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-playfair text-wt-text-primary">User Management</h1>
          <p className="text-wt-text-secondary mt-1">Manage team members and their roles</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => window.location.href = '/roles'}
            className="border-wt-border text-wt-text-primary hover:bg-wt-subtle"
          >
            <Shield className="h-4 w-4 mr-2" />
            Manage Roles
          </Button>
          {canCreateUsers && (
            <CreateUserDialog 
              onSuccess={loadData} 
              roles={roles}
              seatUsage={seatUsage}
              onSeatLimit={(detail) => setUpgradeDialog(detail)}
            />
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-wt-gold/10 rounded-lg">
              <Users className="h-5 w-5 text-wt-gold" />
            </div>
            <div>
              <p className="text-sm text-wt-text-muted">Total Users</p>
              <p className="text-2xl font-semibold text-wt-text-primary">{users.length}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/10 rounded-lg">
              <UserCheck className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-wt-text-muted">Active</p>
              <p className="text-2xl font-semibold text-wt-text-primary">{activeUsers}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-wt-subtle rounded-lg">
              <Shield className="h-5 w-5 text-wt-text-muted" />
            </div>
            <div>
              <p className="text-sm text-wt-text-muted">Roles</p>
              <p className="text-2xl font-semibold text-wt-text-primary">{roles.length}</p>
            </div>
          </div>
        </motion.div>

        {/* Seat usage tile */}
        {seatUsage && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className={`bg-wt-paper border rounded-xl p-4 ${
              seatUsage.remaining <= 0 ? 'border-red-500/40' : seatUsage.remaining <= 1 ? 'border-amber-500/40' : 'border-wt-border'
            }`}
            data-testid="seat-usage-tile"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  seatUsage.remaining <= 0 ? 'bg-red-500/10' : 'bg-blue-500/10'
                }`}>
                  <Sparkles className={`h-5 w-5 ${
                    seatUsage.remaining <= 0 ? 'text-red-400' : 'text-blue-400'
                  }`} />
                </div>
                <div>
                  <p className="text-sm text-wt-text-muted">Seats Used</p>
                  <p className="text-2xl font-semibold text-wt-text-primary" data-testid="seat-usage-count">
                    {seatUsage.used} <span className="text-base text-wt-text-muted">/ {seatUsage.total_limit}</span>
                  </p>
                </div>
              </div>
            </div>
            {seatUsage.addon_seats > 0 && (
              <p className="text-xs text-wt-text-muted mt-2">
                Plan {seatUsage.plan_limit} + {seatUsage.addon_seats} add-on
                <button
                  type="button"
                  onClick={() => handleRemoveAddon(1)}
                  className="ml-2 text-red-400 hover:underline"
                  disabled={purchasingAddon}
                  data-testid="remove-addon-seat"
                >
                  Remove 1
                </button>
              </p>
            )}
            {seatUsage.remaining <= 0 && (
              <button
                type="button"
                onClick={() => setUpgradeDialog({ current: seatUsage.used, limit: seatUsage.total_limit, plan_limit: seatUsage.plan_limit, addon_seats: seatUsage.addon_seats })}
                className="mt-2 text-xs text-wt-gold hover:underline"
                data-testid="seat-upgrade-link"
              >
                Add more seats →
              </button>
            )}
          </motion.div>
        )}
      </div>

      {/* Alert if no roles */}
      {roles.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 flex items-center gap-3"
        >
          <Shield className="h-5 w-5 text-orange-400 flex-shrink-0" />
          <div>
            <p className="text-orange-400 font-medium">No roles created yet</p>
            <p className="text-sm text-wt-text-muted">Create roles first before adding users. 
              <button 
                onClick={() => window.location.href = '/roles'}
                className="text-wt-gold hover:underline ml-1"
              >
                Go to Role Management
              </button>
            </p>
          </div>
        </motion.div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search users..."
          className="pl-10 bg-wt-paper border-wt-border text-wt-text-primary"
        />
      </div>

      {/* Users Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-wt-paper border border-wt-border rounded-xl overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-wt-subtle border-b border-wt-border">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-wt-text-primary uppercase tracking-wider">User</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-wt-text-primary uppercase tracking-wider">Role</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-wt-text-primary uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-wt-text-primary uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-wt-border">
              {filteredUsers.map((user) => {
                const userRole = getUserRole(user);
                const roleTypeConfig = userRole ? (ROLE_TYPE_ICONS[userRole.role_type] || ROLE_TYPE_ICONS.sales) : null;
                const RoleIcon = roleTypeConfig?.icon || Shield;
                
                return (
                  <tr key={user.id} className="hover:bg-wt-subtle transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-wt-gold/20 flex items-center justify-center">
                          <span className="text-wt-gold font-semibold">
                            {user.full_name?.charAt(0).toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-wt-text-primary">{user.full_name}</p>
                          <p className="text-sm text-wt-text-muted">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {userRole ? (
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded ${roleTypeConfig?.bg}`}>
                            <RoleIcon className={`h-4 w-4 ${roleTypeConfig?.color}`} />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-wt-text-primary">{userRole.name}</p>
                            <p className="text-xs text-wt-text-muted capitalize">{userRole.role_type}</p>
                          </div>
                        </div>
                      ) : (
                        <span className="text-wt-text-muted text-sm">No role assigned</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        user.is_active !== false 
                          ? 'bg-green-500/10 text-green-400' 
                          : 'bg-red-500/10 text-red-400'
                      }`}>
                        {user.is_active !== false ? (
                          <><UserCheck className="h-3 w-3" /> Active</>
                        ) : (
                          <><UserX className="h-3 w-3" /> Inactive</>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        {canEditUsers && (
                          <>
                            <EditUserDialog user={user} onSuccess={loadData} roles={roles} />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleToggleStatus(user.id, user.is_active !== false)}
                              className="text-wt-text-muted hover:text-wt-gold"
                              title={user.is_active !== false ? 'Deactivate' : 'Activate'}
                            >
                              {user.is_active !== false ? (
                                <UserX className="h-4 w-4" />
                              ) : (
                                <UserCheck className="h-4 w-4" />
                              )}
                            </Button>
                          </>
                        )}
                        {canDeleteUsers && user.id !== currentUser?.id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-wt-text-muted hover:text-red-400"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="p-12 text-center">
            <Users className="h-12 w-12 mx-auto text-wt-text-muted mb-4" />
            <p className="text-wt-text-muted">No users found</p>
          </div>
        )}
      </motion.div>

      {/* Seat-limit upgrade dialog */}
      <Dialog open={!!upgradeDialog} onOpenChange={(o) => !o && setUpgradeDialog(null)}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md" data-testid="seat-upgrade-dialog">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl flex items-center gap-2">
              <Lock className="h-6 w-6 text-wt-gold" />
              Seat limit reached
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-wt-text-secondary text-sm">
              You're using <span className="font-semibold text-wt-text-primary">{upgradeDialog?.current ?? '—'}</span> of{' '}
              <span className="font-semibold text-wt-text-primary">{upgradeDialog?.limit ?? '—'}</span> available seats on your current plan.
              Add recurring add-on seats — billed at the end of every cycle while they're active.
            </p>
            {upgradeDialog?.addon_seats > 0 && (
              <p className="text-xs text-wt-text-muted">
                Plan includes {upgradeDialog?.plan_limit} seats + {upgradeDialog?.addon_seats} add-on
              </p>
            )}
            <div className="flex flex-col gap-2 pt-2">
              <Button
                onClick={() => handlePurchaseAddon(1)}
                disabled={purchasingAddon}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                data-testid="purchase-addon-1"
              >
                {purchasingAddon ? 'Adding...' : 'Add 1 seat'}
              </Button>
              <Button
                onClick={() => handlePurchaseAddon(5)}
                disabled={purchasingAddon}
                variant="outline"
                className="border-wt-border text-wt-text-primary hover:bg-wt-subtle"
                data-testid="purchase-addon-5"
              >
                {purchasingAddon ? 'Adding...' : 'Add 5 seats (save more)'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => { setUpgradeDialog(null); window.location.href = '/billing'; }}
                className="text-wt-text-secondary"
              >
                Or upgrade your plan →
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagementPage;
